import { useAccount } from "../account/useAccount";
import { useAccounts } from "../account/useAccounts";
import { useEntitlements } from "../account/useEntitlements";

export interface AccountActionPermissions {
  /** Current plan allows teams */
  allowTeams: boolean;
  /** Allow asset transfer */
  allowAssetTransfer: boolean;
  isCompany: boolean;
  isEnterprise: boolean;
  isTeam: boolean;
  /** isCompany || isEnterprise || isTeam */
  isMultiUserAccount: boolean;
  isSharingEnabled: boolean;
  isSuperAdminEnabled: boolean;
  isImportExportEnabled: boolean;
  hasTrialedTeams: boolean;
  isLoading: boolean;
  isChatbotsEnabled: boolean;
}

export function useAccountActionPermissions(): AccountActionPermissions {
  const { account, isLoading: isAccountLoading } = useAccount();
  const { accounts, isLoading: isAccountsLoading } = useAccounts();
  const { entitlements, isLoading: isEntitlementsLoading } = useEntitlements();

  // Companies
  const companiesTrialEnd = entitlements?.planStatus?.companiesTrialEndDate;
  const isTrialingCompanies =
    companiesTrialEnd && new Date(companiesTrialEnd) > new Date();
  const isCompany =
    entitlements?.planStatus?.isCompanyAccount || isTrialingCompanies || false;

  // Enterprise
  const enterpriseTrialEnd = entitlements?.planStatus?.enterpriseTrialEndDate;
  const isTrialingEnterprise =
    enterpriseTrialEnd && new Date(enterpriseTrialEnd) > new Date();
  const isEnterprise =
    entitlements?.planStatus?.isEnterpriseAccount ||
    isTrialingEnterprise ||
    false;

  // Teams
  const teamsTrialEnd = entitlements?.planStatus?.teamsTrialEndDate;
  const isTrialingTeams = teamsTrialEnd && new Date(teamsTrialEnd) > new Date();
  const isTeam =
    entitlements?.planStatus?.isTeamAccount ||
    entitlements?.limits?.isTeamsEnabled ||
    isTrialingTeams ||
    false;
  const hasTrialedTeams = !isTrialingTeams && !!teamsTrialEnd;

  const allowTeams = account?.planInfo?.allowTeams || false;

  const isSuperAdminEnabled =
    entitlements?.limits?.isSuperAdminEnabled || false;
  const isSharingEnabled = entitlements?.limits?.isSharingEnabled || false;
  const isImportExportEnabled = account?.planInfo?.allowImportExport || false;
  const isMultiUserAccount = isTeam || isCompany || isEnterprise;

  // Allow asset transfer if the user has access to more than one account
  const allowAssetTransfer = accounts.length > 1;

  // New entitlements
  const isChatbotsEnabled =
    entitlements?.entitlements?.chatbotsEnabled?.value ?? false;

  return {
    /** Current plan allows teams */
    allowTeams,

    /** Allow asset transfer */
    allowAssetTransfer,

    /** Plan types */
    isCompany,
    isEnterprise,
    isTeam,
    isMultiUserAccount, // isCompany || isEnterprise || isTeam

    /** Account entitlements */
    isSharingEnabled,
    isSuperAdminEnabled,

    /** Plan info */
    isImportExportEnabled,

    /** Trial status */
    hasTrialedTeams,

    isLoading: isAccountLoading || isAccountsLoading || isEntitlementsLoading,

    /** New entitlements */
    isChatbotsEnabled,
  };
}
